import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { History, RotateCcw, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Versteckte Versions-Historie: listet gespeicherte Stände und stellt sie wieder her.
function VersionHistory({ projectId, onClose, onRestored }) {
  const [versions, setVersions] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${BACKEND_URL}/api/projects/${projectId}/versions`)
      .then((r) => {
        if (active) setVersions(r.data.versions || []);
      })
      .catch(() => {
        if (active) setVersions([]);
        toast.error('Verlauf konnte nicht geladen werden');
      });
    return () => {
      active = false;
    };
  }, [projectId]);

  const handleRestore = async (versionId) => {
    setRestoringId(versionId);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/projects/${projectId}/restore/${versionId}`);
      toast.success('Version wiederhergestellt');
      onRestored(r.data);
      onClose();
    } catch (error) {
      console.error('Restore failed:', error);
      toast.error('Wiederherstellen fehlgeschlagen');
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Versions-Verlauf
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-500 -mt-2 mb-2">
          Frühere gespeicherte Stände dieses Drehplans. „Wiederherstellen" setzt den Plan
          zurück – der aktuelle Stand wird vorher automatisch als Version gesichert.
        </p>

        {versions === null ? (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Verlauf…
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-10 text-slate-500">
            <History className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>Noch keine gespeicherten Versionen.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {versions.map((v, idx) => (
              <div
                key={v.version_id}
                className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 text-sm">
                    {v.created_at}
                    {idx === 0 && (
                      <span className="ml-2 text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                        neueste
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{v.summary}</div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRestore(v.version_id)}
                  disabled={restoringId !== null}
                  className="ml-3 shrink-0"
                >
                  {restoringId === v.version_id ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4 mr-1" />
                  )}
                  Wiederherstellen
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default VersionHistory;
